/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import { useMemo } from 'react';
import { MRT_ColumnDef } from 'mantine-react-table';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  ResourceColumn,
  RESOURCE_COLUMNS,
  ResourceTableProps,
  getResourceColumnSchema,
} from '../types';

import useWorkspace from '@features/Workspace/store/hooks/useWorkspace';

import {
  ResourceRowFragmentFragment,
  ResourceType,
} from '@app/types/blackroom-api';
import CellResource from '../cells/CellResource';
import User from '@app/shared/ui-kit/atoms/User';
import CellFavorite from '../cells/CellFavorite';
import { useDateFormatter } from '@app/shared/helpers/hooks/useDateFormatter';
import { Center, Group, Box, Button } from '@mantine/core';
import { formatSize } from '@app/shared/helpers/size';
import CellPublicId from '../cells/CellPublicId';
import CellActions from '../cells/CellActions';
import { ResourceScope } from '@app/types/blackroom-app';
import useAccount from '@app/features/Account/store/useAccount';

/* -------------------------------------------------------------------------- */
/*                               Implementation                               */
/* -------------------------------------------------------------------------- */

export default function useColumns({
  columns,
  scope = 'my',
  tableProps,
  hoveredRowId,
}: {
  columns?: Partial<Record<ResourceColumn, boolean>>;
  scope?: ResourceScope;
  tableProps?: ResourceTableProps;
  hoveredRowId?: string | null;
} = {}) {
  const { t } = useTranslation('Resource');
  const workspace = useWorkspace();
  const { formatDate } = useDateFormatter();

  const { isCurrentWorkspaceAdmin, hasPermission } = useAccount();
  const canSeeAllColumns =
    isCurrentWorkspaceAdmin || hasPermission('manageResources');

  const columnsSchema = columns ?? getResourceColumnSchema(scope);

  const columnDefs = useMemo<{
    columns: MRT_ColumnDef<ResourceRowFragmentFragment>[];
    visibility: Partial<Record<ResourceColumn, boolean>>;
  }>(
    () => ({
      columns: Object.keys(columnsSchema)
        .filter((f) => {
          if (!canSeeAllColumns && f === 'createdBy') {
            return false;
          }

          return RESOURCE_COLUMNS.includes(f as ResourceColumn);
        })
        .map((col) => {
          const sharedColDef: MRT_ColumnDef<ResourceRowFragmentFragment> = {
            enableColumnActions: false,
            enableColumnFilter: false,
            enableSorting: false,
            accessorKey: col,
            grow: false,
            header: t(`ResourceTable.columns.${col}.label`),
          };
          switch (col) {
            case 'name':
              return {
                ...sharedColDef,
                enableSorting: true,
                grow: true,
                Cell: (props) => {
                  const resource = props.row.original;
                  const basePath = `/workspace/${workspace.id}`;
                  const isHovered = hoveredRowId === resource.id;

                  const cellContent = (
                    <Group wrap="nowrap" gap="xs" w="100%">
                      <Box flex={1} miw={0}>
                        <CellResource {...props} />
                      </Box>
                      {isHovered && resource.type === ResourceType.File && (
                        <Button
                          variant="outline"
                          size="xs"
                          color="gray"
                          radius="xs"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            if (tableProps?.onClickResource) {
                              tableProps.onClickResource(props.row.original);
                            }
                          }}
                          style={{
                            flexShrink: 0,
                          }}
                        >
                          {t('ResourceTable.preview')}
                        </Button>
                      )}
                    </Group>
                  );

                  if (tableProps?.withNavigation) {
                    return (
                      <Link
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        to={
                          resource.type === ResourceType.Folder
                            ? `${basePath}/resources/${scope}/${resource.id}`
                            : `${basePath}/file/${resource.id}`
                        }
                        target={
                          resource.type === ResourceType.File
                            ? '_blank'
                            : undefined
                        }
                        style={{ width: '100%', position: 'relative' }}
                      >
                        {cellContent}
                      </Link>
                    );
                  }
                  return cellContent;
                },
                enableGlobalFilter: true,
                accessorFn: (row) => row.name,
              };

            case 'favorite':
              return {
                ...sharedColDef,
                size: 75,
                Cell: (props) => <CellFavorite {...props} scope={scope} />,
                mantineTableHeadCellProps: {
                  style: {
                    padding: '0px',
                  },
                },
              };

            case 'createdBy':
              return {
                ...sharedColDef,
                minSize: 180,
                Cell: ({ row }) => (
                  <Center w="40%">
                    <User size="sm" user={row.original.createdBy} />
                  </Center>
                ),
              };
            case 'index':
              return {
                ...sharedColDef,
                enableSorting: true,
                size: 70,
                mantineTableBodyCellProps: {
                  style: {
                    padding: '0px',
                  },
                },
                mantineTableHeadCellProps: {
                  style: {
                    padding: '0px',
                  },
                },
              };

            case 'publicId':
              return {
                ...sharedColDef,
                size: 100,
                Cell: CellPublicId,
              };

            case 'createdAt':
              return {
                ...sharedColDef,
                enableSorting: true,
                accessorFn: (row) => formatDate(row.createdAt),
              };

            case 'updatedAt':
              return {
                ...sharedColDef,
                enableSorting: true,
                accessorFn: (row) => formatDate(row.updatedAt),
              };

            case 'size':
              return {
                ...sharedColDef,
                size: 100,
                accessorFn: (row) => formatSize(row.size),
              };

            case 'archivedAt':
              return {
                ...sharedColDef,
                enableSorting: true,
                accessorFn: (row) => formatDate(row.archivedAt),
              };

            case 'actions':
              return {
                ...sharedColDef,
                header: '',
                maxSize: 10,
                Cell: CellActions,
              };

            default:
              break;
          }
          return sharedColDef;
        }),
      visibility: columnsSchema,
    }),
    [workspace, scope, columnsSchema, hoveredRowId, tableProps, t],
  );

  return columnDefs;
}
